/**
 * The domain, as zod schemas with types inferred from them.
 *
 * Schemas rather than bare interfaces because this app reads from three places
 * that can all be wrong in ways TypeScript cannot see: an IndexedDB cache
 * written by an older version of the app, a Firestore document written by an
 * older version of the app, and a Cloud Function's payload. `parse` at every
 * boundary turns those into one loud error instead of `undefined.toFixed()`
 * three screens later.
 *
 * TIME IS ALWAYS EPOCH MILLISECONDS. Never a `Timestamp`, never a `Date`.
 * A `Timestamp` is not serialisable, and this app persists its whole query cache
 * to IndexedDB so it can open with no network — the moment one reaches the cache
 * it is written as `{seconds, nanoseconds}` and read back as a plain object with
 * no `.toDate()`. A `Date` survives structuredClone but not JSON, and the
 * functions boundary is JSON. Milliseconds survive both. See `converters.ts`.
 */
import { z } from 'zod';

import { ROLES, VIEW_SCOPES } from '../roles.ts';

/**
 * The languages this product ships in.
 *
 * One definition, because three documents carry a language now — a person's
 * profile, and an invitation, which is written in the language of whoever sent
 * it — and a second copy of the list is a locale the app accepts in one place
 * and rejects in another.
 */
export const Locale = z.enum(['pl', 'en']);
export type Locale = z.infer<typeof Locale>;

/** Epoch milliseconds. The only way a moment is spelled in this codebase. */
export const Millis = z.number().int().finite();

const Id = z.string().min(1);

// ---------------------------------------------------------------------------
// People and tenancy
// ---------------------------------------------------------------------------

/**
 * The two ways the growth of a litter can be read: as the chart, or as the
 * numbers it was drawn from.
 *
 * A preference and not a route, because it is a property of the *reader* and not
 * of the thing being read — a breeder who thinks in figures wants the table on
 * every litter and on every puppy, on their phone and on their tablet, which is
 * why it lives on the profile rather than in the URL or in one device's storage.
 */
export const GrowthView = z.enum(['chart', 'table']);
export type GrowthView = z.infer<typeof GrowthView>;

export const UserProfile = z.object({
  uid: Id,
  displayName: z.string(),
  email: z.email(),
  photoUrl: z.url().nullable(),
  locale: Locale,
  /**
   * Which growth view this person last chose. See `GrowthView`.
   *
   * Written by the client like `locale` and `displayName` — the rules allow an
   * owner to update their own profile as long as `tenantIds` is unchanged — and
   * absent from every document written before the growth section existed, which
   * the app reads as `chart`.
   */
  growthView: GrowthView,
  /**
   * Denormalised: the tenants this person belongs to, so the app can render a
   * tenant switcher from one document instead of a collection-group query it
   * has no permission to run. The Cloud Function that writes a membership
   * writes this in the same transaction; nothing else may touch it.
   */
  tenantIds: z.array(Id),
  createdAt: Millis,
});
export type UserProfile = z.infer<typeof UserProfile>;

export const Tenant = z.object({
  id: Id,
  name: z.string().min(1),
  /** Breed name, free text for now — a breed registry is a later feature. */
  breed: z.string(),
  country: z.string().length(2).nullable(),
  createdBy: Id,
  createdAt: Millis,
});
export type Tenant = z.infer<typeof Tenant>;

/**
 * Which parts of a litter's record a viewer may read — see `VIEW_SCOPES` in
 * platform/src/roles.ts, which is where the meaning of each one is written.
 *
 * On every membership and every invitation, not only on a viewer's, because a
 * role can be changed on a row that already exists and a field that is present
 * for one role and absent for another is a field every reader has to guess
 * about. For anyone who is not a viewer it holds all of them and nothing reads
 * it.
 *
 * A document written before scopes existed has no such field at all. Both the
 * converter and the security rules read that as all of them, which is precisely
 * what such a viewer can see today; migration 002 fills them in so the default
 * stops being load-bearing.
 */
export const ViewScopes = z.array(z.enum(VIEW_SCOPES));

export const Member = z.object({
  /** The document id *is* the uid. Duplicated here so a read is self-contained. */
  uid: Id,
  role: z.enum(ROLES),
  /** See `ViewScopes`. Meaningful only for a `viewer`. */
  scopes: ViewScopes,
  displayName: z.string(),
  invitedBy: Id.nullable(),
  joinedAt: Millis,
});
export type Member = z.infer<typeof Member>;

export const Invite = z.object({
  id: Id,
  tenantId: Id,
  tenantName: z.string(),
  email: z.email(),
  role: z.enum(ROLES),
  /**
   * What this person will be allowed to see once they join, copied onto the
   * membership by `acceptInvite`. See `ViewScopes`.
   *
   * Chosen at the moment of inviting rather than afterwards, because the owner
   * is deciding what to give somebody at exactly the moment they are deciding to
   * give them anything at all — and an invitation that lands first and is scoped
   * later is a window during which the person sees everything.
   */
  scopes: ViewScopes,
  invitedBy: Id,
  createdAt: Millis,
  expiresAt: Millis,
  acceptedAt: Millis.nullable(),
  /**
   * The language the invitation email is written in: the language the INVITER was
   * using when they sent it.
   *
   * Not the invitee's, which nobody knows — they have never opened this app and
   * there is no profile to read a preference from. The person doing the inviting
   * knows whether the person they are inviting reads Polish or English, and the app they are typing
   * into is already in one of the two.
   */
  locale: Locale,
  /**
   * When the invitation email actually went out, and why it did not.
   *
   * On the invitation itself rather than in a log, because the owner is the only
   * person who can act on a failure — resend it, or read the address back to the
   * person over the phone — and a delivery failure they cannot see is an
   * invitation they will assume arrived. Written only by the Cloud Function that
   * sends it; the rules deny every client an update.
   */
  sentAt: Millis.nullable(),
  sendError: z.string().nullable(),
});
export type Invite = z.infer<typeof Invite>;

// ---------------------------------------------------------------------------
// Animals, litters, offspring
// ---------------------------------------------------------------------------

export const Sex = z.enum(['male', 'female']);
export type Sex = z.infer<typeof Sex>;

export const Animal = z.object({
  id: Id,
  name: z.string().min(1),
  sex: Sex,
  /**
   * Nullable, where a litter's is not. An adult reaches this app in two ways:
   * as one of the kennel's own, whose birthday is known, or as the outside stud
   * whose name is on the pedigree and whose date of birth nobody at this end has
   * to hand. Demanding it would get `Date.now()` typed into the field, which is
   * a wrong answer stored as a confident one.
   */
  bornAt: Millis.nullable(),
  /** Registration with the breed registry, when there is one. */
  registration: z.string().nullable(),
  photoPath: z.string().nullable(),
  notes: z.string(),
});
export type Animal = z.infer<typeof Animal>;

/**
 * Where a litter is in its life, and the axis the home screen is grouped by.
 *
 *   incoming    mated and expected. `bornAt` is a due date, and there are no
 *               offspring documents yet — only `expectedCount`.
 *   active      born, and being raised. The litter the app is actually for.
 *   historical  finished: every puppy placed, kept or gone.
 *
 * A stored status rather than one derived from `closedAt` and `bornAt < now`,
 * because "incoming" is a claim the breeder makes and not one a clock can make
 * for them. A litter whelped three days early is the same litter, and a date
 * comparison would have called it active while the bitch was still pregnant.
 */
export const LitterStatus = z.enum(['incoming', 'active', 'historical']);
export type LitterStatus = z.infer<typeof LitterStatus>;

/**
 * How big the adults of this breed get — KEPT FOR THE RECORD, READ BY NOTHING.
 *
 * It used to select the growth thresholds, back when those were a fixed band of
 * percent-per-day per size class. The model does not work that way any more: the
 * relative growth rate of a newborn is breed-independent (see `growth.ts`), so
 * size selects nothing, and what the model wants instead is the dam's own weight
 * — `Litter.damWeightKg`, which gives it five size classes rather than three and
 * is a number rather than a judgement.
 *
 * The field stays in the schema and in the converter so that the answers
 * breeders have already given survive, and so a later calibration pass
 * (nowy-model-wzrostu.md §10, which groups series by size class) has them. No
 * form asks for it any more.
 *
 *   small   under 5 kg grown — Yorkshire terrier, maltese, chihuahua
 *   medium  5 to 25 kg       — beagle, cocker spaniel, border collie
 *   large   over 25 kg       — labrador, golden retriever, german shepherd
 */
export const SizeGroup = z.enum(['small', 'medium', 'large']);
export type SizeGroup = z.infer<typeof SizeGroup>;

export const Litter = z.object({
  id: Id,
  name: z.string().min(1),
  status: LitterStatus,
  /**
   * WHEN the litter was born — date AND time of day. Every age in this litter is
   * measured from it.
   *
   * THE TIME IS NOT DECORATION. The growth model's first two rules are the
   * windows 0–1 and 1–2 days of life, whose thresholds are fractions of a
   * percent (see `growth.ts`); reading them off a midnight-anchored date puts a
   * ±12 hour error on an age that is only 24 hours long. So the litter form asks
   * for an hour, even an approximate one, and the model is honest about a litter
   * whose hour was never recorded: every litter written before this carries
   * midnight, and the interpolation around the early checkpoints degrades its own
   * confidence when the weighings around them are far apart.
   *
   * For an `incoming` litter it is the day the puppies are DUE. The same field
   * rather than a separate `dueAt`, because it is the same day once they arrive:
   * a second date would need keeping in step with the first from the moment it
   * stopped being a guess, and the one that got stale would be the one the
   * growth chart reads.
   */
  bornAt: Millis,
  sireId: Id.nullable(),
  damId: Id.nullable(),
  /**
   * How many are expected, as entered when the litter was created.
   *
   * Kept beside the offspring documents rather than replaced by their count:
   * an `incoming` litter has no documents to count, and once they are born this
   * is what lets the app say "eight expected, seven here".
   */
  expectedCount: z.number().int().nonnegative().nullable(),
  /**
   * The breed of this litter, free text, and may be empty.
   *
   * A second copy of `Tenant.breed` on purpose rather than a lookup up the tree:
   * a kennel's breed is what it mostly whelps and not what it only ever whelps —
   * a co-owned bitch, a second breed taken on, one crossbred litter — and the
   * record of what a litter *was* must not change because the kennel's headline
   * later did. The litter form seeds it from the tenant, so the common case is
   * still typed once.
   *
   * Free text, like the tenant's: this is a label a human reads — with ONE
   * exception the growth model reserves for it: `breedLbwThresholdGrams` in
   * `growth.ts` is keyed by a lower-cased breed name, so a litter whose breed
   * matches an entry transcribed from the literature gets the published
   * low-birth-weight cut-off instead of the fallbacks. That table ships empty,
   * so today nothing branches on this.
   */
  breed: z.string(),
  /**
   * Kept for the record and read by nothing — see `SizeGroup` above.
   */
  sizeGroup: SizeGroup.nullable(),
  /**
   * What the dam weighed BEFORE this mating, in kilograms — not while pregnant,
   * not while nursing. Null until somebody records it.
   *
   * The one number the growth model asks the breeder for, and it buys exactly
   * one thing: whether a newborn is low birth weight. Alonge 2023 showed that
   * birth weight as a percentage of the dam's own mass predicts first-week
   * mortality independently of breed — 4.5% for a toy bitch down to 1.0% for a
   * giant one — and that classification is what selects the early thresholds,
   * which are far stricter for a low-birth-weight puppy than for a normal one.
   * Without it the model falls back to the litter's own median and then to
   * "unknown", which costs a step of confidence and quietens the section.
   *
   * ON THE LITTER AND NOT ON THE DAM, although she is an `Animal` with a
   * document of her own. A bitch's pre-mating weight is different for each
   * litter she has, and a weight edited on her profile would silently re-judge
   * every litter she has ever had — including ones closed years ago. This is a
   * snapshot of a fact at a moment, which is what the litter record is for.
   *
   * Kilograms, where every other weight in this app is grams: it is the number
   * printed on a bathroom scale and read out by a vet, and asking for 12400 g
   * would get 12.4 typed into it.
   */
  damWeightKg: z.number().positive().nullable(),
  /** When it became `historical`. Null in every other status. */
  closedAt: Millis.nullable(),
  notes: z.string(),
  /**
   * A litter has no photographs of its own on this document, and no profile
   * picture either. Its pictures are `PhotoSession` documents, which know the
   * day they were taken; its visual identity on a card or a board comes from
   * the puppies in it.
   */
});
export type Litter = z.infer<typeof Litter>;

export const OffspringStatus = z.enum(['raising', 'reserved', 'placed', 'kept', 'deceased']);
export type OffspringStatus = z.infer<typeof OffspringStatus>;

export const Offspring = z.object({
  id: Id,
  litterId: Id,
  /**
   * May be empty, and for the first week or two usually is. A newborn is
   * identified by its collar long before anybody has settled on a name, and a
   * form that insists on one gets a litter of puppies called "1" to "7".
   */
  name: z.string(),
  sex: Sex,
  /**
   * The collar or ribbon colour breeders actually identify newborns by, before
   * they have names. A hex string so the UI can render the real colour.
   *
   * REQUIRED, where the name is not — this is the identifier. It is how a
   * breeder tells five three-day-old puppies apart with one hand full, and a
   * puppy without one cannot be matched to the animal in the box at all.
   */
  markerColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  /**
   * Birth order within the litter, and the order the dashboard draws them in.
   *
   * A stored number rather than the document id, which for an auto-id is
   * effectively random, and rather than the collar colour, which sorts by
   * nothing a person recognises. Breeders care about the order — the first-born
   * and the last are the two a vet asks about — and a grid that reshuffles
   * itself between two phones is a grid nobody can read a name off.
   */
  sequence: z.number().int().nonnegative(),
  status: OffspringStatus,
  /**
   * What it weighed when it was born, in grams — the anchor of the doubling
   * milestone (see `Litter.doubleByDay`) and the one weight that can never be
   * taken again.
   *
   * NULLABLE IN THE SCHEMA, REQUIRED BY THE FORM, and the gap between those two
   * is deliberate. Every puppy written before this was asked for has none, and a
   * migration inventing one would be inventing the exact number the milestone is
   * judged against. So the schema tolerates null, the readers all say "not
   * recorded" rather than guessing, and the form insists — except on a litter
   * still `incoming`, where a puppy added ahead of the whelping has not been
   * weighed because it has not been born.
   */
  birthWeightGrams: z.number().positive().nullable(),
  microchip: z.string().nullable(),
  /**
   * The profile picture: the single frame drawn in a list, on a card, beside a
   * weight. The one photograph a puppy owns directly, and the only one — every
   * other picture of it is a frame inside a `PhotoSession`, which knows the day
   * it was taken. Chosen on the puppy's own editor, and under no obligation to
   * be one of those frames: a head-and-shoulders shot cropped for a card is not
   * a session, and a session is not a portrait.
   */
  photoPath: z.string().nullable(),
  notes: z.string(),
});
export type Offspring = z.infer<typeof Offspring>;

// ---------------------------------------------------------------------------
// Health and growth
// ---------------------------------------------------------------------------

/**
 * One animal's weight inside a weighing, as it is stored.
 *
 * No id and no moment of its own: both belong to the `MeasurementEntry` this
 * sits in. A litter is weighed in one pass — that is what the wizard does and
 * what a breeder does — so the moment is a property of the *session*, and
 * storing it per animal was seven copies of one fact that two devices could
 * disagree about.
 */
export const MeasurementReading = z.object({
  offspringId: Id,
  grams: z.number().positive(),
  /** Optional: a temperature taken at the same time, in Celsius. */
  temperatureC: z.number().nullable(),
  note: z.string(),
});
export type MeasurementReading = z.infer<typeof MeasurementReading>;

/**
 * ONE WEIGHING OF ONE LITTER: the document the whole growth feature is stored as.
 *
 * This used to be a measurement per animal, in a subcollection under it, and the
 * session that produced them existed only in the wizard's head. Three things
 * were wrong with that, and only the third is about tidiness:
 *
 *   - **it could not be corrected.** A breeder who weighed the litter with the
 *     scale still in kilograms had seven separate records to hunt down, one
 *     pencil at a time, on seven different rows.
 *   - **nobody could be asked about it.** "Who weighed them on Tuesday and
 *     when did they type it in" is a question about the session, and the session
 *     was not a thing. `recordedBy` on each measurement said who wrote *that*
 *     number, and a correction overwrote it.
 *   - eight puppies meant eight listeners over eight collections, because
 *     measurements hung off the animal and a collection-group query cannot be
 *     scoped to a tenant.
 *
 * So the session is the document, `litterId` is on it, and one indexed query per
 * litter answers the chart, the table and the log at once.
 *
 * `recordedAt` is **when the animals were weighed** — the client's own clock,
 * never `serverTimestamp()`, and backdatable to this morning's session typed in
 * at noon. It is what the chart orders by. `createdAt` is when it was actually
 * entered, which is a different fact and the one the log reports next to a name.
 * See CLAUDE.md's invariants for why neither is a server timestamp.
 */
export const MeasurementEntry = z.object({
  id: Id,
  /**
   * Which litter was weighed. A field and not a parent path, for the same reason
   * `Offspring.litterId` is — see `paths.ts` — except here it is also the query:
   * one `where(litterId)` + `orderBy(recordedAt)` feeds every growth view a
   * litter has.
   */
  litterId: Id,
  recordedAt: Millis,
  /**
   * Every animal weighed in this session. May be short of the litter — a puppy
   * asleep on the vet's table is not weighed twice to make the row tidy — and
   * may name an animal since deleted, which readers drop rather than fail on.
   */
  readings: z.array(MeasurementReading),
  /** Who ran the weighing. Never rewritten by a correction; see `updatedBy`. */
  recordedBy: Id,
  /** When it was typed in, as opposed to when the animals were weighed. */
  createdAt: Millis,
  /**
   * Who last corrected it, and when — null on an entry nobody has touched since.
   *
   * A separate pair rather than overwriting `recordedBy`, and that is the whole
   * point of the audit: "the vet weighed them, the owner fixed a digit" is two
   * facts, and the old model could only hold the second one.
   */
  updatedBy: Id.nullable(),
  updatedAt: Millis.nullable(),
});
export type MeasurementEntry = z.infer<typeof MeasurementEntry>;

/**
 * One animal's weighing, as everything downstream of storage reads it.
 *
 * DERIVED, NOT STORED — see `measurements.ts`, which flattens the entries of a
 * litter back into one list per animal. `id` is the id of the entry it came
 * from, which is what lets a point on the chart be traced to the session that
 * recorded it.
 *
 * It stays a type of its own because the growth arithmetic is written against it
 * and is worth keeping ignorant of how weights are laid out on disk: `growth.ts`
 * takes one animal's weighings in time order and says whether it is growing.
 */
export const Measurement = z.object({
  id: Id,
  /**
   * When the animal was actually weighed — the entry's `recordedAt`, set by the
   * client from its own clock and NOT by `serverTimestamp()`. Offline, a server
   * timestamp is null until the write syncs, and a growth chart that drops
   * today's point until the signal comes back is a growth chart nobody trusts.
   */
  recordedAt: Millis,
  grams: z.number().positive(),
  temperatureC: z.number().nullable(),
  note: z.string(),
  /** The entry's `recordedBy` — who ran the weighing this weight came out of. */
  recordedBy: Id,
});
export type Measurement = z.infer<typeof Measurement>;

// ---------------------------------------------------------------------------
// The photographic archive
// ---------------------------------------------------------------------------

/**
 * One animal's frames inside a photographing.
 *
 * No id and no moment of its own, for the same reason `MeasurementReading` has
 * neither: both belong to the session this sits in. A litter is photographed in
 * one pass, so the day is a property of the *session*, and storing it per animal
 * would be seven copies of one fact that two devices could disagree about.
 */
export const PhotoSubject = z.object({
  offspringId: Id,
  /** Storage paths, in the order they were added. */
  paths: z.array(z.string()),
});
export type PhotoSubject = z.infer<typeof PhotoSubject>;

/**
 * ONE PHOTOGRAPHING OF ONE LITTER: what the picture timeline is stored as.
 *
 * A photographic archive is a question about *time* — this is the fortnight-old
 * puppy, this is the same puppy at six weeks — and the album this replaces could
 * not answer it. That album was a flat `photoPaths` array on the litter and
 * another on each puppy: Storage paths with no date on them and no way to
 * acquire one, so a picture uploaded in March and one uploaded in May were two
 * strings next to each other. Those fields are gone from this schema. A document
 * written before they went still carries them; nothing reads them, and Zod drops
 * what it is not asked for. So the session is the document, exactly as a weighing
 * is, and for the same three reasons — it can be corrected, it can be attributed,
 * and it is one query per litter rather than one per animal.
 *
 * `paths` is the litter as a whole: the nest, the group shot, the dam with her
 * puppies. `subjects` is one entry per animal photographed. A session may hold
 * either, both, or a subject the litter has since lost — readers drop unknown
 * offspring ids rather than fail on them, the same way `knownReadings` does.
 *
 * `takenAt` is **the DAY the photographs were taken** — the client's own clock,
 * never `serverTimestamp()`, and backdatable to yesterday's session uploaded
 * this morning. It is what the timeline orders by. `createdAt` is when it was
 * actually uploaded, which is a different fact and the tie-breaker between two
 * sessions dated to the same day.
 *
 * A DAY, NOT A MOMENT, and stored as local midnight of it. A weighing carries
 * its hour because a litter is weighed twice a day and which side of the feed a
 * number was taken on changes what it means; nothing about an archive is read
 * finer than a day, so an hour here would be a field nobody reads and everybody
 * has to answer. The type stays `Millis` because every moment in this codebase
 * is — see the note at the top of this file — and the normalising is the client's
 * (`startOfDay` in app/src/lib/dates.ts).
 */
export const PhotoSession = z.object({
  id: Id,
  /** Which litter was photographed. A field and not a parent path — see `paths.ts`. */
  litterId: Id,
  /** Local midnight of the day they were taken. See above. */
  takenAt: Millis,
  /** Photographs of the litter as a whole, of nobody in particular. */
  paths: z.array(z.string()),
  /** One entry per animal photographed in this session. */
  subjects: z.array(PhotoSubject),
  /** Who took them. Never rewritten by a correction; see `updatedBy`. */
  recordedBy: Id,
  /** When they were uploaded, as opposed to when they were taken. */
  createdAt: Millis,
  updatedBy: Id.nullable(),
  updatedAt: Millis.nullable(),
});
export type PhotoSession = z.infer<typeof PhotoSession>;

// ---------------------------------------------------------------------------
// What is to be done
// ---------------------------------------------------------------------------

/**
 * ONE THING TO DO TO ONE LITTER: worm them on Saturday, book the vet for the
 * six-week check, weigh the small one again tonight.
 *
 * The whole of a newborn litter's husbandry is a calendar — first worming at
 * two weeks, microchips before they go, the vaccination that has to happen on a
 * day and not in a week — and until this there was nowhere to write it down. A
 * `HealthEvent` carries a `dueAt` and is the wrong shape for it twice over: it
 * hangs off one animal where the job is done to the litter, and it is a record
 * of something that HAPPENED with a reminder bolted on, where this is a thing
 * that has not happened yet.
 *
 * `text` is the whole of it — free text, a sentence a breeder types with one
 * hand. No kind, no product, no category. A dropdown of task types is a
 * dropdown somebody has to read past to type "ask about the crooked tail", and
 * the day this needs structure is the day something reads it, which nothing
 * does.
 *
 * `dueAt` IS A DAY, stored as local midnight of it, exactly as `PhotoSession`'s
 * `takenAt` is and for the same reason: nothing about a task is read finer than
 * a day, and an hour nobody reads is an hour somebody has to answer for. It is
 * also what makes the two ways of entering it the same field — a calendar date,
 * or the puppies' day of life, which is that date counted from `Litter.bornAt`.
 * The client converts between them; only the day is stored, because the day of
 * life is a fact about the litter's birthday and would go wrong the moment a
 * whelping date was corrected.
 *
 * THE DUE DATE IS THE CLIENT'S OWN CLOCK, never `serverTimestamp()`. It is what
 * the list orders by and what "due today" is decided against, and a server
 * timestamp is unresolved until the write syncs — so a task typed beside the
 * whelping box would sort to 1970 and read as forty years overdue.
 *
 * `doneAt` is the whole of the state machine: null is outstanding, a moment is
 * done. A boolean beside a date would let the two disagree, and "done, but we do
 * not know when" is not a state anybody needs. Overdue is not stored at all —
 * it is `dueAt` against today, which changes at midnight with nobody writing
 * anything.
 */
export const Task = z.object({
  id: Id,
  /** Which litter this is to be done to. A field and not a parent — see `paths.ts`. */
  litterId: Id,
  /** What to do, as typed. The only description a task has. */
  text: z.string().min(1),
  /** Local midnight of the day it is due. See above. */
  dueAt: Millis,
  /**
   * When it was ticked off, or null while it is still outstanding.
   *
   * The moment and not the day, unlike `dueAt`: this one is only ever read as
   * "most recently done first" in the history, where two things finished on the
   * same afternoon would otherwise be in an order that reshuffles itself.
   */
  doneAt: Millis.nullable(),
  /** Who ticked it off. Null exactly when `doneAt` is. */
  doneBy: Id.nullable(),
  /** Who wrote it down. Never rewritten by an edit; the rules freeze it. */
  createdBy: Id,
  createdAt: Millis,
});
export type Task = z.infer<typeof Task>;

export const EventKind = z.enum([
  'vaccination',
  'deworming',
  'vet-visit',
  'medication',
  'milestone',
  'feeding',
  'note',
]);
export type EventKind = z.infer<typeof EventKind>;

export const HealthEvent = z.object({
  id: Id,
  kind: EventKind,
  /** Client clock, for the same reason as `Measurement.recordedAt`. */
  recordedAt: Millis,
  title: z.string().min(1),
  detail: z.string(),
  /** Product name for a vaccination or wormer; the thing a vet asks for later. */
  product: z.string().nullable(),
  /** When the next dose is due, for the reminder function to pick up. */
  dueAt: Millis.nullable(),
  completedAt: Millis.nullable(),
  recordedBy: Id,
});
export type HealthEvent = z.infer<typeof HealthEvent>;
